import React, { useState, useMemo, useEffect } from "react"
import { NavLink, useLocation } from "react-router-dom"
import {
  Briefcase,
  CheckSquare,
  FileText,
  Calendar,
  ChatCircle,
  CaretDown,
  CaretLeft,
  CaretRight,
  Kanban,
  UserPlus,
  ClipboardText,
  ShieldCheck,
  Question,
  ListChecks,
  Voicemail,
  PhoneCall,
  ChartLine,
  ChartPie,
  Gauge,
  UserCircle,
  Clock,
  Target,
  Warning,
  Robot,
  ChatCentered,
  ArrowsClockwise,
  CreditCard,
  Receipt,
  Bell,
  TextAa,
  UploadSimple,
  Note,
  Signature,
  CalendarCheck,
  MagnifyingGlass,
  Gavel,
  FolderOpen,
  Users,
  Timer,
  CalendarBlank,
  Chalkboard,
  ShieldWarning,
  Tag,
  Lightning,
  HourglassMedium,
  Fingerprint,
} from "@phosphor-icons/react"
import { useUIStore } from "../../stores"
import { useIsDesktop, useModuleEnabled } from "../../hooks"
import { cn } from "../../lib/utils"

// Logo from public folder
const logoImage = process.env.PUBLIC_URL + "/logo.png"

interface NavItem {
  label: string
  href?: string
  icon: React.ElementType
  children?: NavItem[]
}

// Management Dashboard Navigation Structure - Condensed with collapsible groups
function buildNavItems(): NavItem[] {
  return [
    // Top-level items (always visible)
    { label: "Today's Activity", href: "/", icon: Gauge },
    { label: "Call Center", href: "/communication", icon: PhoneCall },
    { label: "Human Actions", href: "/human-actions", icon: Robot },
    { label: "User Activity", href: "/user-activity", icon: UserCircle },

    // Collapsible groups
    {
      label: "Analytics",
      icon: ChartLine,
      children: [
        { label: "Historical", href: "/historical/daily", icon: ChartLine },
        { label: "Month To Date", href: "/month-to-date", icon: Calendar },
        { label: "Pipeline Breakdown", href: "/pipeline-breakdown", icon: ChartPie },
        { label: "Claimables", href: "/claimables", icon: ClipboardText },
      ],
    },
    {
      label: "Billing",
      icon: CreditCard,
      children: [
        { label: "Payments", href: "/payments", icon: CreditCard },
        { label: "Delinquent Matters", href: "/delinquent", icon: Warning },
        { label: "Recurring Plans", href: "/subscriptions", icon: ArrowsClockwise },
        { label: "Plans Over Time", href: "/subscriptions/over-time", icon: ChartLine },
        { label: "Expired Methods", href: "/expired-methods", icon: CreditCard },
        { label: "Revenue Projections", href: "/revenue-projections", icon: Receipt },
        { label: "Matters Without Plan", href: "/matters-without-plan", icon: MagnifyingGlass },
        { label: "Billing Automation", href: "/billing-automation", icon: Lightning },
      ],
    },
    {
      label: "Time & Billing",
      icon: Timer,
      children: [
        { label: "Time Reports", href: "/time-reports", icon: Timer },
        { label: "Overview", href: "/time-lookup", icon: CalendarBlank },
        { label: "User Report", href: "/user-billing-report", icon: ChartPie },
        { label: "Categories", href: "/time-categories", icon: Tag },
      ],
    },
    {
      label: "Quality Control",
      icon: Target,
      children: [
        { label: "User Effectiveness", href: "/user-effectiveness", icon: Target },
        { label: "User Phone Calls", href: "/phone-calls-users", icon: PhoneCall },
        { label: "Expected Dates", href: "/expected-dates", icon: Clock },
        { label: "Event Calls", href: "/event-calls", icon: Calendar },
        { label: "Items Calls", href: "/items-calls", icon: ListChecks },
        { label: "Matters Overdue", href: "/matters-overdue", icon: Warning },
      ],
    },
    {
      label: "Action Required",
      icon: Bell,
      children: [
        { label: "Approvals", href: "/approvals", icon: ShieldCheck },
        { label: "Requests", href: "/requests", icon: Question },
        { label: "Tasks", href: "/tasks", icon: CheckSquare },
        { label: "Outstanding Items", href: "/outstanding-items", icon: ListChecks },
        { label: "General Items", href: "/general-items", icon: ListChecks },
      ],
    },
    {
      label: "Communications",
      icon: ChatCircle,
      children: [
        { label: "Phone Calls", href: "/calls", icon: PhoneCall },
        { label: "Hourly Volume", href: "/phone-calls-hourly", icon: ChartLine },
        { label: "Voicemails", href: "/voicemails", icon: Voicemail },
        { label: "Text Messages", href: "/text-threads", icon: ChatCircle },
        { label: "Undelivered Texts", href: "/undelivered-texts", icon: TextAa },
        { label: "Follow Ups", href: "/follow-ups", icon: ArrowsClockwise },
        { label: "Messaging", href: "/messaging", icon: ChatCentered },
      ],
    },
    {
      label: "Documents",
      icon: FileText,
      children: [
        { label: "Uploads", href: "/uploads", icon: FileText },
        { label: "Document Uploads", href: "/document-uploads", icon: UploadSimple },
        { label: "Signatures", href: "/signatures", icon: Signature },
        { label: "Field Audits", href: "/field-audits", icon: ShieldWarning },
      ],
    },
    {
      label: "Pipeline",
      icon: Kanban,
      children: [
        { label: "Leads", href: "/leads", icon: UserPlus },
        { label: "Bookings", href: "/bookings", icon: Briefcase },
        { label: "User Pipelines", href: "/user-pipelines", icon: Kanban },
      ],
    },
    {
      label: "Deep Dive",
      icon: MagnifyingGlass,
      children: [
        { label: "User Notes", href: "/user-notes", icon: Note },
        { label: "Events", href: "/events", icon: Calendar },
        { label: "Matter Alerts", href: "/matter-alerts", icon: Bell },
      ],
    },
    {
      label: "Training",
      icon: Chalkboard,
      children: [
        { label: "Overview", href: "/training", icon: Chalkboard },
        { label: "User Progress", href: "/training/users", icon: Users },
      ],
    },
  ]
}

// Tooltip component for collapsed mode
function Tooltip({ children, label, collapsed }: {
  children: React.ReactNode
  label: string
  collapsed: boolean
}) {
  if (!collapsed) return <>{children}</>

  return (
    <div className="group relative">
      {children}
      <div className="sidebar-tooltip">
        {label}
      </div>
    </div>
  )
}

function NavItemComponent({
  item,
  collapsed,
}: {
  item: NavItem
  collapsed: boolean
}) {
  const location = useLocation()
  const hasChildren = item.children && item.children.length > 0

  // Check if any child route is active
  const hasActiveChild = useMemo(() => {
    if (!item.children) return false
    return item.children.some(
      child => child.href && (
        location.pathname === child.href ||
        location.pathname.startsWith(child.href + "/")
      )
    )
  }, [item.children, location.pathname])

  // Auto-expand if any child is active
  const [isOpen, setIsOpen] = useState(hasActiveChild)

  // Re-expand when route changes to a child
  useEffect(() => {
    if (hasActiveChild) setIsOpen(true)
  }, [hasActiveChild])

  // Expandable nav group (not collapsed sidebar)
  if (hasChildren && !collapsed) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-open={isOpen}
          className={cn(
            "nav-group-trigger group flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] font-medium transition-all duration-150",
            "text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-hover",
            isOpen && "bg-sidebar-hover/50 text-sidebar-text-active",
            hasActiveChild && "text-sidebar-text-active"
          )}
        >
          <item.icon
            className={cn(
              "h-[18px] w-[18px] shrink-0 transition-all duration-150",
              isOpen || hasActiveChild ? "opacity-100 text-primary" : "opacity-75 group-hover:opacity-100"
            )}
          />
          <span className="flex-1 text-left">{item.label}</span>
          <CaretDown
            className={cn(
              "h-3.5 w-3.5 opacity-50 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>
        <div
          className={cn(
            "overflow-hidden transition-all duration-200 ease-out",
            isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="nav-group-children ml-[15px] mt-1 space-y-0.5 border-l border-sidebar-border/50 pl-2.5">
            {item.children?.map((child) => (
              <NavLink
                key={child.href}
                to={child.href!}
                end
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-2 py-[5px] text-[12px] font-medium transition-all duration-150",
                    isActive
                      ? "text-sidebar-text-active bg-sidebar-active"
                      : "text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover"
                  )
                }
              >
                <span>{child.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Collapsed sidebar with children - show flyout on hover
  if (hasChildren && collapsed) {
    return (
      <div className="group relative">
        <button
          className={cn(
            "flex w-full items-center justify-center rounded-md px-2 py-[7px] transition-all duration-150",
            "text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-hover",
            hasActiveChild && "text-primary bg-sidebar-active"
          )}
        >
          <item.icon className={cn(
            "h-[18px] w-[18px] shrink-0",
            hasActiveChild ? "opacity-100" : "opacity-75 group-hover:opacity-100"
          )} />
        </button>
        {/* Flyout menu */}
        <div className="nav-flyout invisible absolute left-full top-0 z-[300] ml-2 min-w-[180px] rounded-lg bg-sidebar-gradient-from p-2 opacity-0 shadow-lg transition-all duration-150 group-hover:visible group-hover:opacity-100">
          <div className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-section-label">
            {item.label}
          </div>
          <div className="space-y-0.5">
            {item.children?.map((child) => (
              <NavLink
                key={child.href}
                to={child.href!}
                end
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-2 py-[5px] text-[12px] font-medium transition-all duration-150",
                    isActive
                      ? "text-sidebar-text-active bg-sidebar-active"
                      : "text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover"
                  )
                }
              >
                <span>{child.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Regular nav item (no children)
  return (
    <Tooltip label={item.label} collapsed={collapsed}>
      <NavLink
        to={item.href!}
        end
        className={({ isActive }) =>
          cn(
            "group flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] font-medium transition-all duration-150",
            collapsed ? "justify-center px-2" : "",
            isActive
              ? "nav-item-active text-sidebar-text-active bg-sidebar-active"
              : "text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-hover"
          )
        }
      >
        <item.icon
          className={cn(
            "h-[18px] w-[18px] shrink-0 transition-opacity",
            "opacity-75 group-hover:opacity-100"
          )}
        />
        {!collapsed && (
          <span className="flex-1">{item.label}</span>
        )}
      </NavLink>
    </Tooltip>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, setSidebarCollapsed, mobileMenuOpen, setMobileMenuOpen } = useUIStore()
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const isBkEnabled = useModuleEnabled("bk")

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname, setMobileMenuOpen])

  // Compute effective collapsed state based on screen size
  // Mobile/Tablet: always expanded (drawer is 280px)
  // Desktop: respect user preference
  const isCollapsed = isDesktop ? sidebarCollapsed : false

  // Build nav items with optional module-specific groups
  const navItems = useMemo(() => {
    const items = buildNavItems()

    // Insert module-specific groups after top-level items (position 4 = after User Activity)
    // This keeps them prominent but grouped with other collapsible sections
    const moduleInsertIndex = 4

    // Add BK collapsible group if module is enabled for the selected division
    if (isBkEnabled) {
      items.splice(moduleInsertIndex, 0, {
        label: "Bankruptcy",
        icon: Gavel,
        children: [
          { label: "Daily Reports", href: "/bk/daily-reports", icon: CalendarCheck },
          { label: "Tasks / Items", href: "/bk/items", icon: ListChecks },
          { label: "Docket Search", href: "/bk/docket-search", icon: MagnifyingGlass },
          { label: "Cases Filed", href: "/bk/cases-filed", icon: FolderOpen },
          { label: "Hearings", href: "/bk/hearings", icon: Gavel },
          { label: "Filing Fee Deadlines", href: "/bk/filing-fee-deadlines", icon: Clock },
          { label: "Trustees", href: "/bk/trustees", icon: Users },
          { label: "Trustee Uploads", href: "/bk/trustee-uploads", icon: UploadSimple },
          { label: "Case Touch", href: "/bk/case-touch", icon: Fingerprint },
          { label: "13s Pending", href: "/bk/13s-pending", icon: HourglassMedium },
          { label: "7s Pending", href: "/bk/7s-pending", icon: HourglassMedium },
        ],
      })
    }

    return items
  }, [isBkEnabled])

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-[200] flex flex-col transition-all duration-300 ease-out",
        "bg-gradient-to-b from-sidebar-gradient-from to-sidebar-gradient-to",
        "shadow-[4px_0_24px_rgba(0,0,0,0.15)] dark:shadow-[2px_0_8px_rgba(0,0,0,0.6)]",
        // Mobile/Tablet: drawer that slides in from left
        "w-[280px] -translate-x-full",
        mobileMenuOpen && "translate-x-0",
        // Desktop: always visible, respect collapse state
        "lg:translate-x-0",
        sidebarCollapsed ? "lg:w-16" : "lg:w-[200px]"
      )}
    >
      {/* Logo */}
      <div className="flex h-[56px] items-center justify-between border-b border-sidebar-border px-4">
        {!isCollapsed ? (
          <div className="flex items-center gap-3">
            <img src={logoImage} alt="IntuiDesk" className="h-8 w-8" />
            <span className="text-[16px] font-bold tracking-wide text-sidebar-text-active">
              INTUIDESK
            </span>
          </div>
        ) : (
          <div className="mx-auto">
            <img src={logoImage} alt="IntuiDesk" className="h-8 w-8" />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-2 scrollbar-dark">
        <div className="space-y-0.5">
          {navItems.map((item) => (
            <NavItemComponent
              key={item.href || item.label}
              item={item}
              collapsed={isCollapsed}
            />
          ))}
        </div>
      </nav>

      {/* Collapse toggle - hidden on mobile/tablet, only shown on desktop */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className={cn(
          "hidden lg:flex h-11 items-center justify-center border-t border-sidebar-border",
          "text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover",
          "transition-colors duration-150"
        )}
      >
        {sidebarCollapsed ? (
          <CaretRight className="h-4 w-4" />
        ) : (
          <CaretLeft className="h-4 w-4" />
        )}
      </button>
    </aside>
  )
}
